import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Edit, Image, Play, Plus, Trash2, Upload } from 'lucide-react';
import { RichTextEditor } from '@/components/ui/RichTextEditor';
import { api, API } from '@/api';
import { resolveMediaUrl } from '@/utils/helpers';
import { MediaPickerDialog } from './AdminShared';

const AdminMediaLibrary = () => {
  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [filterTag, setFilterTag] = useState('');

  const loadMedia = useCallback(async () => {
    try {
      const res = await api.getMedia(filterTag);
      setMedia(res.data);
    } catch (error) {
      console.error('Error loading media:', error);
    }
  }, [filterTag]);

  useEffect(() => {
    loadMedia();
  }, [loadMedia]);

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files.length) return;

    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('alt_text', file.name);
        formData.append('tags', '');
        await api.uploadMedia(formData);
      }
      loadMedia();
    } catch (error) {
      alert('Error uploading file');
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this image?')) {
      try {
        await api.deleteMedia(id);
        loadMedia();
      } catch (error) {
        alert('Error deleting media');
      }
    }
  };

  const copyUrl = (url) => {
    const fullUrl = resolveMediaUrl(url);
    navigator.clipboard.writeText(fullUrl);
    alert('URL copied to clipboard!');
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">
          <Image className="w-6 h-6 inline mr-2" /> Media Library ({media.length})
        </h2>
        <div className="flex gap-2">
          <Input 
            placeholder="Filter by tag..." 
            value={filterTag}
            onChange={(e) => setFilterTag(e.target.value)}
            className="bg-gray-700 border-gray-600 text-white w-48"
          />
          <label className="cursor-pointer">
            <input 
              type="file" 
              multiple 
              accept="image/*" 
              onChange={handleUpload} 
              className="hidden" 
            />
            <Button className="bg-sky-400 hover:bg-sky-500 text-gray-900" disabled={uploading} asChild>
              <span><Upload className="w-4 h-4 mr-2" /> {uploading ? 'Uploading...' : 'Upload Images'}</span>
            </Button>
          </label>
        </div>
      </div>

      <Card className="bg-gray-800/50 border-sky-900/30">
        <CardContent className="p-6">
          {media.length === 0 ? (
            <div className="text-center py-12">
              <Image className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">No images uploaded yet</p>
              <p className="text-gray-500">Upload images to use for backgrounds, logos, and thumbnails</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {media.map((item) => (
                <div 
                  key={item.id} 
                  className="group relative aspect-square bg-gray-700 rounded-lg overflow-hidden cursor-pointer"
                  onClick={() => setSelectedMedia(item)}
                >
                  <img 
                    src={resolveMediaUrl(item.url)} 
                    alt={item.alt_text || item.original_name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <Button size="sm" variant="secondary" onClick={(e) => { e.stopPropagation(); copyUrl(item.url); }}>
                      Copy URL
                    </Button>
                    <Button size="sm" variant="destructive" onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-black/70 px-2 py-1 text-xs text-white truncate">
                    {item.original_name}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Media Detail Modal */}
      <Dialog open={!!selectedMedia} onOpenChange={() => setSelectedMedia(null)}>
        <DialogContent className="max-w-2xl bg-gray-900 border-sky-900/50">
          <DialogHeader>
            <DialogTitle className="text-white">Image Details</DialogTitle>
          </DialogHeader>
          {selectedMedia && (
            <div className="space-y-4">
              <div className="aspect-video bg-gray-800 rounded-lg overflow-hidden">
                <img 
                  src={resolveMediaUrl(selectedMedia.url)} 
                  alt={selectedMedia.alt_text}
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-gray-400">Filename</Label>
                  <p className="text-white">{selectedMedia.original_name}</p>
                </div>
                <div>
                  <Label className="text-gray-400">Size</Label>
                  <p className="text-white">{formatFileSize(selectedMedia.file_size)}</p>
                </div>
                <div className="col-span-2">
                  <Label className="text-gray-400">URL</Label>
                  <div className="flex gap-2">
                    <Input 
                      value={resolveMediaUrl(selectedMedia.url)}
                      readOnly
                      className="bg-gray-700 border-gray-600 text-white text-sm"
                    />
                    <Button size="sm" onClick={() => copyUrl(selectedMedia.url)}>Copy</Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Admin Advertising Management

const AdminNews = ({ leagues, teams, onRefresh }) => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingArticle, setEditingArticle] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    featured_image: '',
    author: '',
    league_ids: [],
    team_ids: [],
    is_published: false
  });

  useEffect(() => {
    loadArticles();
  }, []);

  const loadArticles = async () => {
    try {
      const res = await api.getNewsAdmin();
      setArticles(res.data);
    } catch (error) {
      console.error('Error loading articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNew = () => {
    setEditingArticle(null);
    setFormData({
      title: '',
      content: '',
      excerpt: '',
      featured_image: '',
      author: '',
      league_ids: [],
      team_ids: [],
      is_published: false
    });
    setShowForm(true);
  };

  const handleEdit = (article) => {
    setEditingArticle(article);
    setFormData({
      title: article.title || '',
      content: article.content || '',
      excerpt: article.excerpt || '',
      featured_image: article.featured_image || '',
      author: article.author || '',
      league_ids: article.league_ids || [],
      team_ids: article.team_ids || [],
      is_published: article.is_published || false
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formData.title || !formData.content) {
      alert('Title and content are required');
      return;
    }
    setSaving(true);
    try {
      if (editingArticle) {
        await api.updateNews(editingArticle.id, formData);
      } else {
        await api.createNews(formData);
      }
      await loadArticles();
      setShowForm(false);
      setEditingArticle(null);
    } catch (error) {
      console.error('Error saving article:', error);
      alert('Failed to save article');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;
    try {
      await api.deleteNews(id);
      await loadArticles();
    } catch (error) {
      console.error('Error deleting article:', error);
    }
  };

  const toggleLeague = (leagueId) => {
    setFormData(prev => ({
      ...prev,
      league_ids: prev.league_ids.includes(leagueId)
        ? prev.league_ids.filter(id => id !== leagueId)
        : [...prev.league_ids, leagueId]
    }));
  };

  const toggleTeam = (teamId) => {
    setFormData(prev => ({
      ...prev,
      team_ids: prev.team_ids.includes(teamId)
        ? prev.team_ids.filter(id => id !== teamId)
        : [...prev.team_ids, teamId]
    }));
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">News Articles</h2>
        <Button onClick={handleNew} className="bg-sky-500 hover:bg-sky-600">
          <Plus className="w-4 h-4 mr-2" /> New Article
        </Button>
      </div>

      {/* Article Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-gray-900 border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingArticle ? 'Edit Article' : 'New Article'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-gray-300">Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="bg-gray-800 border-gray-700 text-white"
                placeholder="Article title"
              />
            </div>

            <div>
              <Label className="text-gray-300">Excerpt (optional)</Label>
              <Input
                value={formData.excerpt}
                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                className="bg-gray-800 border-gray-700 text-white"
                placeholder="Brief summary for previews"
              />
            </div>

            <div>
              <Label className="text-gray-300">Featured Image</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={formData.featured_image}
                  onChange={(e) => setFormData({ ...formData, featured_image: e.target.value })}
                  className="bg-gray-800 border-gray-700 text-white flex-1"
                  placeholder="Image URL"
                />
                <Button type="button" variant="outline" onClick={() => setShowMediaPicker(true)} className="border-gray-600">
                  <Image className="w-4 h-4 mr-1" /> Browse
                </Button>
              </div>
              {formData.featured_image && (
                <img src={resolveMediaUrl(formData.featured_image)} alt="" className="mt-2 h-32 rounded object-cover" />
              )}
            </div>

            <div>
              <Label className="text-gray-300">Content *</Label>
              <RichTextEditor
                content={formData.content}
                onChange={(html) => setFormData({ ...formData, content: html })}
                placeholder="Start writing your article..."
              />
            </div>

            <div>
              <Label className="text-gray-300">Author</Label>
              <Input
                value={formData.author}
                onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                className="bg-gray-800 border-gray-700 text-white"
                placeholder="Author name"
              />
            </div>

            <div>
              <Label className="text-gray-300">Tag Leagues</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {leagues.map(league => (
                  <button
                    key={league.id}
                    type="button"
                    onClick={() => toggleLeague(league.id)}
                    className={`px-3 py-1 rounded-full text-sm transition-colors ${
                      formData.league_ids.includes(league.id)
                        ? 'bg-sky-500 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    {league.name}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-gray-300">Tag Teams</Label>
              <div className="flex flex-wrap gap-2 mt-2 max-h-32 overflow-y-auto">
                {teams.map(team => (
                  <button
                    key={team.id}
                    type="button"
                    onClick={() => toggleTeam(team.id)}
                    className={`px-3 py-1 rounded-full text-sm transition-colors ${
                      formData.team_ids.includes(team.id)
                        ? 'bg-sky-500 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    {team.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_published"
                checked={formData.is_published}
                onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="is_published" className="text-gray-300 cursor-pointer">
                Publish immediately
              </Label>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowForm(false)} className="border-gray-600">
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving} className="bg-sky-500 hover:bg-sky-600">
                {saving ? 'Saving...' : (editingArticle ? 'Update' : 'Create')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Media Picker */}
      <MediaPickerDialog
        open={showMediaPicker}
        onClose={() => setShowMediaPicker(false)}
        onSelect={(url) => {
          setFormData({ ...formData, featured_image: url });
          setShowMediaPicker(false);
        }}
      />

      {/* Articles List */}
      {loading ? (
        <div className="grid gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-gray-800/50 rounded-lg p-4 animate-pulse">
              <div className="h-6 bg-gray-700 rounded w-1/2 mb-2" />
              <div className="h-4 bg-gray-700 rounded w-3/4" />
            </div>
          ))}
        </div>
      ) : articles.length === 0 ? (
        <div className="bg-gray-800/50 rounded-lg p-8 text-center border border-gray-700">
          <p className="text-gray-400">No articles yet. Create your first news article!</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {articles.map(article => (
            <div key={article.id} className="bg-gray-800/50 rounded-lg p-4 border border-gray-700 flex gap-4">
              {article.featured_image && (
                <img 
                  src={resolveMediaUrl(article.featured_image)} 
                  alt="" 
                  className="w-24 h-24 rounded object-cover flex-shrink-0"
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="text-white font-semibold truncate">{article.title}</h3>
                    <p className="text-gray-400 text-sm truncate">{article.excerpt || 'No excerpt'}</p>
                  </div>
                  <Badge className={article.is_published ? 'bg-green-500' : 'bg-yellow-500'}>
                    {article.is_published ? 'Published' : 'Draft'}
                  </Badge>
                </div>
                <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                  {article.author && <span>By {article.author}</span>}
                  {article.published_at && (
                    <span>{new Date(article.published_at).toLocaleDateString()}</span>
                  )}
                  {article.league_ids?.length > 0 && (
                    <span>{article.league_ids.length} leagues tagged</span>
                  )}
                </div>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" variant="outline" onClick={() => handleEdit(article)} className="border-gray-600 text-gray-300">
                    <Edit className="w-3 h-3 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleDelete(article.id)} className="border-red-600 text-red-400 hover:bg-red-600/20">
                    <Trash2 className="w-3 h-3 mr-1" /> Delete
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============== ADMIN PODCAST ==============
const AdminPodcast = ({ onRefresh }) => {
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingEpisode, setEditingEpisode] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [mediaPickerTarget, setMediaPickerTarget] = useState('thumbnail'); // 'thumbnail' or 'audio'
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    audio_url: '',
    youtube_url: '',
    thumbnail: '',
    duration: '',
    episode_number: '',
    season: '1',
    is_published: false
  });

  const rssUrl = `${API}/podcast/rss`;

  useEffect(() => {
    loadEpisodes();
  }, []);

  const loadEpisodes = async () => {
    try {
      const res = await api.getPodcastEpisodesAdmin();
      setEpisodes(res.data);
    } catch (error) {
      console.error('Error loading episodes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNew = () => {
    setEditingEpisode(null);
    setFormData({
      title: '',
      description: '',
      audio_url: '',
      youtube_url: '',
      thumbnail: '',
      duration: '',
      episode_number: episodes.length + 1,
      season: '1',
      is_published: false
    });
    setShowForm(true);
  };

  const handleEdit = (episode) => {
    setEditingEpisode(episode);
    setFormData({
      title: episode.title || '',
      description: episode.description || '',
      audio_url: episode.audio_url || '',
      youtube_url: episode.youtube_url || '',
      thumbnail: episode.thumbnail || '',
      duration: episode.duration || '',
      episode_number: episode.episode_number || '',
      season: episode.season || '1',
      is_published: episode.is_published || false
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formData.title || !formData.description || !formData.audio_url) {
      alert('Title, description, and audio file are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...formData,
        duration: formData.duration ? parseInt(formData.duration) : null,
        episode_number: formData.episode_number ? parseInt(formData.episode_number) : null,
        season: formData.season ? parseInt(formData.season) : 1
      };
      
      if (editingEpisode) {
        await api.updatePodcastEpisode(editingEpisode.id, payload);
      } else {
        await api.createPodcastEpisode(payload);
      }
      await loadEpisodes();
      setShowForm(false);
      setEditingEpisode(null);
    } catch (error) {
      console.error('Error saving episode:', error);
      alert('Failed to save episode');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this episode?')) return;
    try {
      await api.deletePodcastEpisode(id);
      await loadEpisodes();
    } catch (error) {
      console.error('Error deleting episode:', error);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    formDataUpload.append('tag', 'podcast');
    
    setUploading(true);
    try {
      const res = await api.uploadMedia(formDataUpload);
      setFormData({ ...formData, audio_url: res.data.url });
      
      // Try to get duration from audio file
      const audio = new Audio(resolveMediaUrl(res.data.url));
      audio.addEventListener('loadedmetadata', () => {
        setFormData(prev => ({ ...prev, duration: Math.round(audio.duration) }));
      });
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Podcast Episodes</h2>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.open(rssUrl, '_blank')} className="border-orange-500 text-orange-400">
            RSS Feed
          </Button>
          <Button onClick={handleNew} className="bg-sky-500 hover:bg-sky-600">
            <Plus className="w-4 h-4 mr-2" /> New Episode
          </Button>
        </div>
      </div>

      {/* RSS Feed Info */}
      <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
        <p className="text-orange-300 text-sm">
          <strong>RSS Feed URL:</strong> {rssUrl}
        </p>
        <p className="text-gray-400 text-xs mt-1">
          Use this URL to submit your podcast to Apple Podcasts, Spotify, and other platforms.
        </p>
      </div>

      {/* Episode Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-gray-900 border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingEpisode ? 'Edit Episode' : 'New Episode'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">Season</Label>
                <Input
                  type="number"
                  value={formData.season}
                  onChange={(e) => setFormData({ ...formData, season: e.target.value })}
                  className="bg-gray-800 border-gray-700 text-white"
                  min="1"
                />
              </div>
              <div>
                <Label className="text-gray-300">Episode Number</Label>
                <Input
                  type="number"
                  value={formData.episode_number}
                  onChange={(e) => setFormData({ ...formData, episode_number: e.target.value })}
                  className="bg-gray-800 border-gray-700 text-white"
                  min="1"
                />
              </div>
            </div>

            <div>
              <Label className="text-gray-300">Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="bg-gray-800 border-gray-700 text-white"
                placeholder="Episode title"
              />
            </div>

            <div>
              <Label className="text-gray-300">Description *</Label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full h-32 bg-gray-800 border border-gray-700 text-white rounded-md p-3"
                placeholder="Episode description"
              />
            </div>

            <div>
              <Label className="text-gray-300">Audio File (MP3) *</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={formData.audio_url}
                  onChange={(e) => setFormData({ ...formData, audio_url: e.target.value })}
                  className="bg-gray-800 border-gray-700 text-white flex-1"
                  placeholder="Audio URL or upload"
                />
                <label className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-md cursor-pointer flex items-center gap-1">
                  <Upload className="w-4 h-4" />
                  {uploading ? 'Uploading...' : 'Upload'}
                  <input
                    type="file"
                    accept="audio/*"
                    onChange={handleFileUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                </label>
              </div>
              {formData.audio_url && (
                <audio controls src={resolveMediaUrl(formData.audio_url)} className="w-full mt-2" />
              )}
            </div>

            <div>
              <Label className="text-gray-300">Duration (seconds)</Label>
              <Input
                type="number"
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                className="bg-gray-800 border-gray-700 text-white"
                placeholder="Duration in seconds (auto-detected on upload)"
              />
            </div>

            <div>
              <Label className="text-gray-300">YouTube URL (optional)</Label>
              <Input
                value={formData.youtube_url}
                onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
                className="bg-gray-800 border-gray-700 text-white"
                placeholder="https://youtube.com/watch?v=..."
              />
            </div>

            <div>
              <Label className="text-gray-300">Thumbnail</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={formData.thumbnail}
                  onChange={(e) => setFormData({ ...formData, thumbnail: e.target.value })}
                  className="bg-gray-800 border-gray-700 text-white flex-1"
                  placeholder="Thumbnail image URL"
                />
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => { setMediaPickerTarget('thumbnail'); setShowMediaPicker(true); }} 
                  className="border-gray-600"
                >
                  <Image className="w-4 h-4" />
                </Button>
              </div>
              {formData.thumbnail && (
                <img src={resolveMediaUrl(formData.thumbnail)} alt="" className="mt-2 h-24 rounded object-cover" />
              )}
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="ep_published"
                checked={formData.is_published}
                onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="ep_published" className="text-gray-300 cursor-pointer">
                Publish immediately
              </Label>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowForm(false)} className="border-gray-600">
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving} className="bg-sky-500 hover:bg-sky-600">
                {saving ? 'Saving...' : (editingEpisode ? 'Update' : 'Create')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Media Picker */}
      <MediaPickerDialog
        open={showMediaPicker}
        onClose={() => setShowMediaPicker(false)}
        onSelect={(url) => {
          if (mediaPickerTarget === 'thumbnail') {
            setFormData({ ...formData, thumbnail: url });
          }
          setShowMediaPicker(false);
        }}
      />

      {/* Episodes List */}
      {loading ? (
        <div className="grid gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-gray-800/50 rounded-lg p-4 animate-pulse">
              <div className="h-6 bg-gray-700 rounded w-1/2 mb-2" />
              <div className="h-4 bg-gray-700 rounded w-3/4" />
            </div>
          ))}
        </div>
      ) : episodes.length === 0 ? (
        <div className="bg-gray-800/50 rounded-lg p-8 text-center border border-gray-700">
          <p className="text-gray-400">No episodes yet. Create your first podcast episode!</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {episodes.map(episode => (
            <div key={episode.id} className="bg-gray-800/50 rounded-lg p-4 border border-gray-700 flex gap-4">
              {episode.thumbnail ? (
                <img 
                  src={resolveMediaUrl(episode.thumbnail)} 
                  alt="" 
                  className="w-24 h-24 rounded object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-24 h-24 rounded bg-gray-700 flex items-center justify-center flex-shrink-0">
                  <Play className="w-8 h-8 text-gray-500" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <Badge className="bg-sky-500/20 text-sky-300 text-xs mb-1">
                      S{episode.season || 1} E{episode.episode_number || '?'}
                    </Badge>
                    <h3 className="text-white font-semibold truncate">{episode.title}</h3>
                    <p className="text-gray-400 text-sm line-clamp-2">{episode.description}</p>
                  </div>
                  <Badge className={episode.is_published ? 'bg-green-500' : 'bg-yellow-500'}>
                    {episode.is_published ? 'Published' : 'Draft'}
                  </Badge>
                </div>
                <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                  <span>{formatDuration(episode.duration)}</span>
                  {episode.published_at && (
                    <span>{new Date(episode.published_at).toLocaleDateString()}</span>
                  )}
                  {episode.youtube_url && <span>Has YouTube</span>}
                </div>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" variant="outline" onClick={() => handleEdit(episode)} className="border-gray-600 text-gray-300">
                    <Edit className="w-3 h-3 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleDelete(episode.id)} className="border-red-600 text-red-400 hover:bg-red-600/20">
                    <Trash2 className="w-3 h-3 mr-1" /> Delete
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Admin Clubs Manager - Score Tracking & Graphics

export { AdminMediaLibrary, AdminNews, AdminPodcast };
